(() => {
  const register = () => {
    if (customElements.get('smxb-drawer')) return;
    const ThemeDrawer = customElements.get('drawer-component');
    class SpiegelmaxDrawer extends ThemeDrawer {
      handleShowTransition(animate = true) {
        return this.transitionDrawer(true, animate);
      }

      handleHideTransition() {
        return this.transitionDrawer(false, true);
      }

      transitionDrawer(entering, animate) {
        this.smxbMotion?.abort();
        const controller = new AbortController();
        this.smxbMotion = controller;
        const reduced = !animate || matchMedia('(prefers-reduced-motion: reduce)').matches;
        const inner = this.querySelector('.drawer__inner');
        if (entering) inner?.getBoundingClientRect();
        if (reduced) {
          this.toggleAttribute('active', entering);
          return Promise.resolve();
        }
        return new Promise(resolve => {
          let timer, frame, finished = false;
          const finish = () => {
            if (finished) return;
            finished = true;
            clearTimeout(timer);
            cancelAnimationFrame(frame);
            controller.abort();
            if (this.smxbMotion === controller) this.smxbMotion = null;
            resolve();
          };
          controller.signal.addEventListener('abort', finish, { once: true });
          this.overlay.addEventListener('transitionend', event => {
            if (event.target === this.overlay && event.propertyName === 'opacity') finish();
          }, { signal: controller.signal });
          const duration = node => node ? Math.max(0, ...getComputedStyle(node).transitionDuration.split(',').map(value => parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000))) : 0;
          const wait = Math.max(duration(inner), duration(this.overlay));
          const apply = () => {
            if (controller.signal.aborted) return;
            this.toggleAttribute('active', entering);
            timer = setTimeout(finish, wait + 80);
          };
          if (entering) frame = requestAnimationFrame(() => {
            frame = requestAnimationFrame(apply);
          });
          else apply();
        });
      }

      handleAfterShow() {
        if (!this.open || this.smxbCounted) return;
        this.smxbCounted = true;
        super.handleAfterShow();
      }

      handleAfterHide() {
        if (this.open) return;
        if (this.smxbCounted) {
          this.smxbCounted = false;
          super.handleAfterHide();
        } else if (!document.querySelector('.drawer[open],.modal[open]')) {
          document.body.classList.remove('modal-showing');
          FoxTheme.a11y.removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : null);
        }
      }
    }
    customElements.define('smxb-drawer', SpiegelmaxDrawer);
  };
  if (customElements.get('drawer-component')) register();
  else customElements.whenDefined('drawer-component').then(register);
})();

if (!customElements.get('smxb-header')) {
  class SpiegelmaxTestHeader extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.input = this.querySelector('[data-search-input]');
      this.form = this.querySelector('.smxb-search-form');
      this.results = this.querySelector('[data-search-results]');
      this.sheet = this.querySelector('[data-search-sheet]');
      this.initialResults = this.results.innerHTML;
      this.status = this.querySelector('[data-search-status]');
      this.help = this.querySelector('.smxb-help');
      this.menu = this.querySelector('[data-menu-dialog]');
      this.wishlist = this.querySelector('[data-wishlist-dialog]');
      this.themeProperties = ['--header-height', '--header-offset-top', '--header-navigation-height', '--header-group-height'];
      this.previousThemeProperties = this.themeProperties.map(name => [name, document.documentElement.style.getPropertyValue(name)]);
      this.wasPinned = document.body.classList.contains('header-pinned');
      this.cache = new Map();
      this.savedPriceCache = new Map();
      this.saved = this.readSaved();
      this.recent = this.readRecent();
      this.panelHistory = [];
      this.activePanel = 'root';
      this.on(this, 'click', event => this.nativeCartClick(event), { capture: true });
      this.on(this, 'click', event => this.handleClick(event));
      this.on(this.input, 'focus', () => this.openSearch());
      this.on(this.input, 'input', () => this.handleInput());
      this.on(this.input, 'keydown', event => this.searchKeys(event));
      this.on(this.form, 'submit', event => {
        const term = this.input.value.trim();
        if (!term) { event.preventDefault(); this.input.focus(); }
        else this.rememberSearch(term);
      });
      this.on(document, 'keydown', event => this.documentKeys(event));
      this.on(document, 'click', event => {
        const button = event.target.closest?.('[data-card-save]');
        if (!button || !this.wishlist) return;
        event.preventDefault();
        event.stopPropagation();
        this.toggleSaved(button);
      }, { capture: true });
      this.on(document, 'click', event => {
        if (this.help && !this.help.contains(event.target)) this.help.open = false;
      });
      this.on(window, 'resize', () => { this.queueMeasure(); this.updateScrollLock(); });
      this.on(window, 'scroll', () => this.queueMeasure(true), { passive: true });
      this.on(window, 'storage', event => {
        if (event.key === 'smxb-header-wishlist-v1') { this.saved = this.readSaved(); this.renderSaved(); }
        if (event.key === 'smxb-header-recent-v1') { this.recent = this.readRecent(); this.renderRecent(); }
      });
      this.on(document, 'shopify:block:select', async event => {
        if (event.detail?.sectionId !== this.dataset.sectionId) return;
        const blockId = event.detail.blockId;
        if ((this.dataset.menuIconBlocks || '').split(',').includes(blockId)) {
          const panels = Array.from(this.menu.querySelectorAll('[data-menu-panel]'));
          const icon = [...this.menu.querySelectorAll('[data-menu-icon-block]'), ...this.querySelectorAll('[data-menu-icon-block]')].find(node => node.dataset.menuIconBlock === blockId);
          const navigationKey = icon?.closest('[data-start-panel]')?.dataset.startPanel;
          const panel = icon?.closest('[data-menu-panel]') || panels.find(node => node.dataset.menuPanel === navigationKey);
          if (!panel) return;
          this.closeSearch(false);
          if (this.wishlist?.open) await this.closeDialog(this.wishlist);
          this.panelHistory = [];
          let ancestor = panel;
          while (ancestor?.dataset.parentPanel && this.panelHistory.length < 10) {
            this.panelHistory.unshift(ancestor.dataset.parentPanel);
            ancestor = panels.find(node => node.dataset.menuPanel === ancestor.dataset.parentPanel);
          }
          this.showPanel(panel.dataset.menuPanel, false);
          this.openDialog(this.menu, this.querySelector('[data-menu-open]'));
          return;
        }
        if (!(this.dataset.discoveryBlocks || '').split(',').includes(blockId)) return;
        await Promise.all([this.menu, this.wishlist].filter(drawer => drawer?.open).map(drawer => this.closeDialog(drawer)));
        this.input.value = '';
        this.restoreInitial();
        this.openSearch();
        this.input.focus();
      });
      for (const drawer of [this.menu, this.wishlist].filter(Boolean)) {
        this.on(drawer, 'click', event => { if (!this.contains(event.target)) this.handleClick(event); });
        this.on(drawer, 'drawer:handleAfterHide', () => {
          this.querySelectorAll('[data-menu-open]').forEach(button => button.setAttribute('aria-expanded', 'false'));
          if (drawer === this.wishlist) this.savedObserver?.disconnect();
        });
        this.on(drawer, 'pointerdown', event => { drawer.smxbBackdropPress = event.target.matches('[data-dialog-backdrop]'); });
        this.on(drawer, 'click', event => {
          if (drawer.smxbBackdropPress && event.target.matches('[data-dialog-backdrop]')) this.closeDialog(drawer);
        });
      }
      if (this.help) {
        this.on(this.help, 'pointerenter', event => {
          if (event.pointerType === 'mouse' && matchMedia('(min-width:1200px) and (hover:hover)').matches && !this.searching) this.help.open = true;
        });
        this.on(this.help, 'pointerleave', event => {
          if (event.pointerType === 'mouse' && matchMedia('(min-width:1200px) and (hover:hover)').matches) this.help.open = false;
        });
        this.on(this.help, 'toggle', () => { if (this.help.open) { this.closeSearch(false); this.measure(); } });
      }
      this.observer = new ResizeObserver(() => this.queueMeasure());
      this.observer.observe(this.querySelector('.smxb-row'));
      const navigation = this.querySelector('.smxb-bottomnav');
      if (navigation) this.observer.observe(navigation);
      document.querySelectorAll('.shopify-section-group-header-group').forEach(node => this.observer.observe(node));
      this.measure();
      this.renderSaved();
      this.renderRecent();
      this.cardSaveObserver = new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('[data-card-save]') || node.querySelector('[data-card-save]'))))) this.queueCardSaveSync();
      });
      this.cardSaveObserver.observe(document.body, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.request?.abort();
      this.observer?.disconnect();
      this.savedObserver?.disconnect();
      this.cardSaveObserver?.disconnect();
      cancelAnimationFrame(this.cardSaveFrame);
      document.querySelectorAll('[data-card-save]').forEach(button => { button.hidden = true; });
      for (const drawer of [this.menu, this.wishlist].filter(Boolean)) {
        if (drawer.open) {
          Promise.resolve(drawer.hide()).then(() => { if (!this.isConnected) drawer.remove(); });
        } else if (drawer.parentElement === document.body) drawer.remove();
      }
      clearTimeout(this.debounce);
      cancelAnimationFrame(this.measureFrame);
      document.documentElement.classList.remove('smxb-scroll-lock');
      for (const [name, value] of this.previousThemeProperties || []) {
        if (value) document.documentElement.style.setProperty(name, value);
        else document.documentElement.style.removeProperty(name);
      }
      document.body.classList.toggle('header-pinned', Boolean(this.wasPinned));
    }

    on(target, name, callback, options = {}) {
      target?.addEventListener(name, callback, { ...options, signal: this.signal });
    }

    openDialog(drawer, opener) {
      if (!drawer || drawer.open || typeof drawer.show !== 'function') return;
      const shown = drawer.show(opener || document.activeElement);
      if (drawer === this.wishlist) this.observeSavedPrices();
      return shown;
    }

    closeDialog(drawer) {
      if (drawer?.open && typeof drawer.hide === 'function') return drawer.hide();
      return Promise.resolve();
    }

    queueMeasure(positionsOnly = false) {
      if (!positionsOnly) this.layoutDirty = true;
      if (this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        const updateLayout = this.layoutDirty || !this.layoutMetrics;
        this.layoutDirty = false;
        this.measure(updateLayout);
      });
    }

    setMeasureProperty(target, name, value) {
      if (target.style.getPropertyValue(name) !== value) target.style.setProperty(name, value);
    }

    measure(updateLayout = true) {
      const box = this.getBoundingClientRect();
      if (updateLayout || !this.layoutMetrics) {
        const row = this.querySelector('.smxb-row').getBoundingClientRect();
        const navigation = this.querySelector('.smxb-bottomnav');
        const section = this.closest('.shopify-section');
        const groupHeight = Array.from(document.querySelectorAll('.shopify-section-group-header-group')).reduce((sum, node) => sum + node.offsetHeight, 0);
        const navigationHeight = Math.round(navigation?.offsetHeight || 0);
        this.layoutMetrics = {
          rowBottom: row.bottom - box.top,
          navigationBottom: navigationHeight > 0 ? navigation.getBoundingClientRect().bottom - box.top : null,
          headerHeight: Math.round(this.offsetHeight),
          sectionTop: Math.round(section?.offsetTop || 0),
          navigationHeight,
          groupHeight: Math.round(groupHeight)
        };
      }
      const metrics = this.layoutMetrics;
      this.setMeasureProperty(this, '--smxb-header-bottom', `${Math.round(Math.max(0, box.top + metrics.rowBottom))}px`);
      this.setMeasureProperty(this, '--smxb-navigation-bottom', `${metrics.navigationBottom === null ? 0 : Math.round(Math.max(0, box.top + metrics.navigationBottom))}px`);
      this.setMeasureProperty(this, '--smxb-menu-top', `${Math.round(Math.max(0, box.top))}px`);
      if (updateLayout) {
        this.setMeasureProperty(document.documentElement, '--header-height', `${metrics.headerHeight}px`);
        this.setMeasureProperty(document.documentElement, '--header-offset-top', `${metrics.sectionTop}px`);
        this.setMeasureProperty(document.documentElement, '--header-navigation-height', `${metrics.navigationHeight}px`);
        this.setMeasureProperty(document.documentElement, '--header-group-height', `${metrics.groupHeight}px`);
      }
      const pinned = this.dataset.stickyType !== 'none' && box.top <= 0;
      if (document.body.classList.contains('header-pinned') !== pinned) document.body.classList.toggle('header-pinned', pinned);
    }

    nativeCartClick(event) {
      const trigger = event.target.closest?.('[data-menu-open],[data-wishlist-open]');
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        this.handleClick(event);
        return;
      }
      const link = event.target.closest?.('[data-native-cart]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
      const drawer = document.getElementById(link.getAttribute('aria-controls'));
      if (typeof drawer?.show !== 'function') return;
      event.preventDefault();
      event.stopPropagation();
      this.closeSearch(false);
      if (this.help) this.help.open = false;
      drawer.show(link);
    }

    handleClick(event) {
      const target = event.target.closest?.('button,a');
      if (!target || (!this.contains(target) && !this.menu?.contains(target) && !this.wishlist?.contains(target))) return;
      if (target.matches('[data-menu-open]')) {
        this.closeSearch(false);
        if (this.help) this.help.open = false;
        this.panelHistory = [];
        const panel = target.dataset.startPanel || 'root';
        if (panel !== 'root') this.panelHistory.push('root');
        this.showPanel(panel, false);
        this.measure();
        this.openDialog(this.menu, target);
        target.setAttribute('aria-expanded', 'true');
        this.updateScrollLock();
      } else if (target.matches('[data-panel-back]')) {
        this.showPanel(this.panelHistory.pop() || target.dataset.panelTarget || 'root', true);
      } else if (target.matches('[data-panel-target]')) {
        this.panelHistory.push(this.activePanel);
        this.showPanel(target.dataset.panelTarget, true);
      } else if (target.matches('[data-dialog-close]')) {
        this.closeDialog(target.closest('smxb-drawer'));
      } else if (target.matches('[data-search-close]')) {
        this.closeSearch(true);
      } else if (target.matches('[data-search-clear],[data-search-reset]')) {
        this.input.value = '';
        this.restoreInitial();
        this.input.focus();
      } else if (target.matches('[data-search-term]')) {
        event.preventDefault();
        this.input.value = target.dataset.searchTerm;
        this.input.focus();
        this.handleInput(true);
      } else if (target.matches('[data-suggested-query]')) {
        this.rememberSearch(target.dataset.suggestedQuery);
      } else if (target.matches('[data-clear-recent]')) {
        this.recent = [];
        try { localStorage.removeItem('smxb-header-recent-v1'); } catch (_) {}
        this.renderRecent();
      } else if (target.matches('[data-wishlist-open]')) {
        this.closeSearch(false);
        if (this.help) this.help.open = false;
        this.renderSaved();
        this.openDialog(this.wishlist, target);
        this.updateScrollLock();
      } else if (target.matches('[data-save-product]')) {
        this.toggleSaved(target);
      } else if (target.matches('[data-remove-product]')) {
        this.saved = this.saved.filter(item => item.id !== target.dataset.removeProduct);
        this.persistSaved();
        this.renderSaved();
        this.wishlist?.querySelector('[data-dialog-close]')?.focus();
      }
    }

    showPanel(key, focus) {
      const panels = Array.from(this.menu.querySelectorAll('[data-menu-panel]'));
      const active = panels.find(panel => panel.dataset.menuPanel === key);
      if (!active) return;
      for (const panel of panels) panel.hidden = panel !== active;
      this.activePanel = key;
      this.menu.querySelector('.smxb-menu-scroll').scrollTop = 0;
      if (focus) (active.querySelector('h2') || active.querySelector('button,a'))?.focus();
    }

    updateScrollLock() {
      const locked = this.searching && matchMedia('(max-width:1199px)').matches;
      document.documentElement.classList.toggle('smxb-scroll-lock', Boolean(locked));
    }

    openSearch() {
      if (this.searching) return;
      this.style.setProperty('--smxb-resting-height', `${this.offsetHeight}px`);
      this.searching = true;
      if (this.help) this.help.open = false;
      this.classList.add('is-searching');
      this.sheet.hidden = false;
      for (const button of this.querySelectorAll('[data-search-close]')) button.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.measure();
      this.updateScrollLock();
      this.renderRecent();
      if (this.input.value.trim()) this.handleInput(true);
    }

    closeSearch(returnFocus) {
      if (!this.searching) return;
      this.searching = false;
      clearTimeout(this.debounce);
      this.request?.abort();
      this.classList.remove('is-searching');
      this.sheet.hidden = true;
      for (const button of this.querySelectorAll('[data-search-close]')) button.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.results.removeAttribute('aria-busy');
      this.updateScrollLock();
      if (returnFocus) this.querySelector('.smxb-search-submit')?.focus({ preventScroll: true });
      else if (document.activeElement === this.input) this.input.blur();
      this.queueMeasure();
    }

    restoreInitial() {
      clearTimeout(this.debounce);
      this.request?.abort();
      this.results.innerHTML = this.initialResults;
      this.querySelector('[data-search-clear]').hidden = !this.input.value.trim();
      this.results.removeAttribute('aria-busy');
      this.status.textContent = this.input.value.trim() ? this.dataset.labelMinimum : '';
      this.renderRecent();
    }

    handleInput(immediate = false) {
      clearTimeout(this.debounce);
      this.request?.abort();
      const term = this.input.value.trim();
      this.querySelector('[data-search-clear]').hidden = !term;
      if (term.length < 2) { this.restoreInitial(); return; }
      this.debounce = setTimeout(() => this.loadResults(term), immediate ? 0 : 180);
    }

    async loadResults(term) {
      if (this.cache.has(term)) { this.displayResults(this.cache.get(term)); return; }
      const controller = new AbortController();
      this.request = controller;
      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.labelLoading;
      try {
        const endpoint = new URL(this.dataset.predictiveUrl, location.origin);
        const types = ['product', 'query'];
        if (this.dataset.showCollections === 'true') types.push('collection');
        if (this.dataset.showGuides === 'true') types.push('page', 'article');
        endpoint.searchParams.set('q', term);
        endpoint.searchParams.set('resources[type]', types.join(','));
        endpoint.searchParams.set('resources[limit]', this.dataset.resultLimit || '6');
        endpoint.searchParams.set('resources[limit_scope]', 'each');
        endpoint.searchParams.set('resources[options][unavailable_products]', 'last');
        endpoint.searchParams.set('section_id', 'smxb-search-results');
        const response = await fetch(endpoint, { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search preview unavailable');
        const html = await response.text();
        if (controller.signal.aborted || term !== this.input.value.trim() || !this.searching) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('.smxb-predictive');
        if (!content) throw new Error('Missing search results');
        if (this.cache.size >= 12) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(term, content.outerHTML);
        this.displayResults(content.outerHTML);
      } catch (error) {
        if (error.name === 'AbortError' || controller.signal.aborted) return;
        this.results.innerHTML = this.initialResults;
        const link = document.createElement('a');
        const url = new URL(this.dataset.searchUrl, location.origin);
        url.searchParams.set('q', term);
        link.href = url.href;
        link.className = 'smxb-all-results';
        link.textContent = this.dataset.labelError;
        this.results.append(link);
        this.status.textContent = '';
        this.renderRecent();
      } finally {
        if (this.request === controller) this.results.removeAttribute('aria-busy');
      }
    }

    displayResults(html) {
      this.results.innerHTML = html;
      this.results.removeAttribute('aria-busy');
      this.status.textContent = this.results.querySelector('[data-search-summary]')?.dataset.searchSummary || '';
      this.syncSaveButtons();
    }

    searchKeys(event) {
      if (!this.searching || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      const targets = Array.from(this.results.querySelectorAll('a,button')).filter(node => node.getClientRects().length);
      if (!targets.length) return;
      event.preventDefault();
      (event.key === 'ArrowDown' ? targets[0] : targets[targets.length - 1]).focus();
    }

    documentKeys(event) {
      if (event.key === 'Escape') {
        if (this.searching) { event.preventDefault(); this.closeSearch(true); }
        else if (this.help?.open) { this.help.open = false; this.help.querySelector('summary').focus(); }
      }
      if (event.key !== 'Tab' || !this.searching || !this.contains(document.activeElement)) return;
      const elements = Array.from(this.querySelectorAll('a[href],button,input')).filter(node => !node.disabled && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden' && !node.matches('.smxb-search-backdrop'));
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }

    cleanSearch(value) {
      return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim().slice(0, 80) : '';
    }

    readRecent() {
      if (this.dataset.recentEnabled !== 'true') return [];
      try {
        const value = JSON.parse(localStorage.getItem('smxb-header-recent-v1') || '[]');
        return Array.isArray(value) ? value.map(term => this.cleanSearch(term)).filter(term => term.length >= 2 && !term.includes('@')).slice(0, 5) : [];
      } catch (_) { return []; }
    }

    rememberSearch(value) {
      if (this.dataset.recentEnabled !== 'true') return;
      const term = this.cleanSearch(value);
      if (term.length < 2 || term.includes('@') || /\d{7,}/.test(term)) return;
      this.recent = [term, ...this.recent.filter(previous => previous.toLocaleLowerCase() !== term.toLocaleLowerCase())].slice(0, 5);
      try { localStorage.setItem('smxb-header-recent-v1', JSON.stringify(this.recent)); } catch (_) {}
    }

    renderRecent() {
      const group = this.results.querySelector('[data-recent-searches]');
      if (!group) return;
      const list = group.querySelector('[data-recent-list]');
      group.hidden = !this.recent.length;
      list.replaceChildren();
      for (const term of this.recent) {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.searchTerm = term;
        button.textContent = term;
        item.append(button);
        list.append(item);
      }
    }

    normalizeSaved(value) {
      if (!value || !value.id || typeof value.title !== 'string' || typeof value.url !== 'string') return null;
      try {
        const url = new URL(value.url, location.origin);
        if (url.origin !== location.origin || !/\/products\/[^/]+/.test(url.pathname)) return null;
        let image = '';
        if (typeof value.image === 'string' && value.image) {
          const imageURL = new URL(value.image, location.origin);
          if (imageURL.protocol === 'https:' && (imageURL.hostname === location.hostname || imageURL.hostname === 'cdn.shopify.com')) image = imageURL.href;
        }
        return { id: String(value.id).slice(0, 40), title: value.title.slice(0, 300), url: url.pathname, image };
      } catch (_) { return null; }
    }

    readSaved() {
      try {
        const stored = JSON.parse(localStorage.getItem('smxb-header-wishlist-v1') || '[]');
        return Array.isArray(stored) ? stored.map(value => this.normalizeSaved(value)).filter(Boolean).slice(0, 40) : [];
      } catch (_) { return []; }
    }

    persistSaved() {
      try { localStorage.setItem('smxb-header-wishlist-v1', JSON.stringify(this.saved)); } catch (_) { /* Memory-only fallback when storage is unavailable. */ }
    }

    toggleSaved(button) {
      if (!this.wishlist) return;
      let product;
      try { product = this.normalizeSaved(JSON.parse(button.dataset.saveProduct)); } catch (_) { return; }
      if (!product) return;
      const exists = this.saved.some(item => item.id === product.id);
      this.saved = exists ? this.saved.filter(item => item.id !== product.id) : [product, ...this.saved].slice(0, 40);
      this.persistSaved();
      this.renderSaved();
      this.querySelector('[data-wishlist-status]').textContent = exists ? this.dataset.labelRemove : this.dataset.labelSaved;
    }

    queueCardSaveSync() {
      if (this.cardSaveFrame) return;
      this.cardSaveFrame = requestAnimationFrame(() => {
        this.cardSaveFrame = null;
        this.syncSaveButtons();
      });
    }

    syncSaveButtons() {
      for (const button of new Set([...this.querySelectorAll('[data-save-product]'), ...(this.wishlist?.querySelectorAll('[data-save-product]') || []), ...document.querySelectorAll('[data-card-save]')])) {
        if (!this.wishlist) { button.hidden = true; continue; }
        try {
          const product = JSON.parse(button.dataset.saveProduct);
          const selected = this.saved.some(item => item.id === String(product.id));
          button.hidden = false;
          button.setAttribute('aria-pressed', String(selected));
          const actionLabel = selected ? this.dataset.labelRemove : this.dataset.labelSave;
          if (button.hasAttribute('data-card-save')) {
            button.setAttribute('aria-label', `${this.dataset.labelSave}: ${product.title}`);
            button.title = actionLabel;
          } else button.setAttribute('aria-label', actionLabel);
          const label = button.querySelector('[data-save-label]');
          if (label) label.textContent = selected ? this.dataset.labelCurrentSaved : this.dataset.labelCurrentSave;
        } catch (_) { button.hidden = true; }
      }
    }

    renderSaved() {
      const count = this.querySelector('[data-wishlist-count]');
      if (count) { count.textContent = String(this.saved.length); count.hidden = !this.saved.length; }
      this.syncSaveButtons();
      if (!this.wishlist) return;
      this.savedObserver?.disconnect();
      this.wishlist.querySelector('[data-wishlist-empty]').hidden = this.saved.length > 0;
      const list = this.wishlist.querySelector('[data-wishlist-list]');
      list.replaceChildren();
      for (const product of this.saved) {
        const item = document.createElement('li');
        item.className = 'smxb-saved-item';
        item.dataset.savedUrl = product.url;
        const imageLink = document.createElement('a');
        imageLink.href = product.url;
        imageLink.tabIndex = -1;
        imageLink.setAttribute('aria-hidden', 'true');
        if (product.image) {
          const img = document.createElement('img');
          img.src = product.image; img.alt = ''; img.width = 100; img.height = 100; img.loading = 'lazy';
          imageLink.append(img);
        }
        const content = document.createElement('div');
        content.className = 'smxb-saved-content';
        const title = document.createElement('a');
        title.className = 'smxb-saved-title';
        title.href = product.url; title.textContent = product.title;
        const details = document.createElement('div');
        details.className = 'smxb-saved-price';
        details.setAttribute('data-saved-price', '');
        const link = document.createElement('a');
        link.className = 'smxb-saved-view';
        link.href = product.url; link.textContent = this.dataset.labelView;
        const arrow = this.wishlist.querySelector('[data-saved-arrow]').content.firstElementChild.cloneNode(true);
        link.append(arrow);
        content.append(title, details, link);
        const remove = document.createElement('button');
        remove.type = 'button'; remove.dataset.removeProduct = product.id;
        remove.setAttribute('aria-label', `${this.dataset.labelRemove}: ${product.title}`);
        remove.append(this.wishlist.querySelector('[data-dialog-close] svg').cloneNode(true));
        item.append(imageLink, content, remove);
        list.append(item);
      }
      if (this.wishlist.open) this.observeSavedPrices();
    }

    observeSavedPrices() {
      if (!this.wishlist?.open) return;
      this.savedObserver?.disconnect();
      this.savedObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.savedObserver.unobserve(entry.target);
          this.hydrateSavedPrice(entry.target);
        }
      }, { root: this.wishlist.querySelector('.smxb-wishlist-body'), rootMargin: '80px' });
      this.wishlist.querySelectorAll('[data-saved-url]').forEach(item => this.savedObserver.observe(item));
    }

    async hydrateSavedPrice(item) {
      const path = item.dataset.savedUrl;
      if (!this.savedPriceCache.has(path)) {
        if (this.savedPriceCache.size >= 40) this.savedPriceCache.delete(this.savedPriceCache.keys().next().value);
        const request = (async () => {
          try {
            const endpoint = new URL(path, location.origin);
            endpoint.searchParams.set('section_id', 'smxb-saved-product');
            const response = await fetch(endpoint, { signal: this.signal, headers: { Accept: 'text/html' } });
            if (!response.ok) return null;
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            return doc.querySelector('[data-saved-detail]');
          } catch (_) { return null; }
        })();
        this.savedPriceCache.set(path, request);
      }
      const detail = await this.savedPriceCache.get(path);
      if (!detail || !item.isConnected || this.signal.aborted) return;
      item.querySelector('[data-saved-price]').replaceChildren(...Array.from(detail.childNodes).map(node => node.cloneNode(true)));
      if (detail.dataset.currentTitle) item.querySelector('.smxb-saved-title').textContent = detail.dataset.currentTitle;
    }

  }
  customElements.define('smxb-header', SpiegelmaxTestHeader);
}



